<template>
  <div
    ref="rootRef"
    class="panels"
  >
    <slot />
  </div>
</template>

<script setup>
import { ref, computed, watch, provide, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useSwipe } from './useSwipe.js'

defineOptions({ name: 'Panels' })

const emit = defineEmits(['update:modelValue', 'beforeTransition', 'transition'])

const props = defineProps({
  modelValue: { type: String, required: true },
  animated: { type: Boolean, default: true },
  swipeable: { type: Boolean, default: false },
  infinite: { type: Boolean, default: false },
  vertical: { type: Boolean, default: false },
  transitionPrev: { type: String, default: 'slide-right' },
  transitionNext: { type: String, default: 'slide-left' },
  transitionDuration: { type: Number, default: 300 },
  keepAlive: { type: Boolean, default: false },
  keepAliveInclude: { type: [String, Array, RegExp], default: undefined },
  keepAliveExclude: { type: [String, Array, RegExp], default: undefined },
  keepAliveMax: { type: Number, default: undefined }
})

const $router = useRouter()
const $route = useRoute()

let transitionTimer = null
let routeWatchStop = null

const panels = ref([])
const prevActive = ref(null)
const rootRef = ref(null)
const routePanels = ref([])

const register = (name, isDisabled) => {
  if (!panels.value.find(p => p.name === name)) {
    panels.value.push({
      idx: panels.value.length,
      name,
      isDisabled
    })
  }
}

const unregister = name => {
  panels.value = panels.value.filter(p => p.name !== name).map((p, idx) => ({ ...p, idx }))
}

const goTo = name => emit('update:modelValue', name)

const goToPanelByOffset = (direction, startIndex) => {
  const ps = panels.value
  const start = startIndex ?? ps.findIndex(p => p.name === props.modelValue)
  let index = start + direction

  while (index >= 0 && index < ps.length) {
    if (!ps[index].isDisabled()) {
      emit('update:modelValue', ps[index].name)
      return
    }
    index += direction
  }

  if (props.infinite && ps.length > 0 && start !== -1 && start !== ps.length)
    goToPanelByOffset(direction, direction === -1 ? ps.length : -1)
}

const includesParams = (outer, inner) => {
  for (const k in inner) {
    if (inner[k] !== outer[k]) return false
  }
  return true
}

const getActiveLinkIndex = (resolved, currentMatched) => {
  const last = resolved.matched[resolved.matched.length - 1]
  if (!last) return -1
  return currentMatched.findIndex(m => (last.aliasOf || last) === (m.aliasOf || m))
}

const updateActiveRoute = () => {
  if (!routePanels.value.length) return
  let name = null
  let best = { matchedLen: 0, queryDiff: 9999, hrefLen: 0 }
  const { query: currentQuery, hash: currentHash, matched: currentMatched } = $route
  const currentQueryLen = Object.keys(currentQuery).length

  for (const rp of routePanels.value) {
    let resolved
    try {
      resolved = $router.resolve(rp.to)
    } catch {
      continue
    }
    if (!resolved) continue

    const activeIdx = getActiveLinkIndex(resolved, currentMatched)
    if (activeIdx === -1) continue
    const isActive = includesParams($route.params, resolved.params)
    const isExactActive = isActive && activeIdx === currentMatched.length - 1
    if (rp.exact && !isExactActive) continue
    if (!rp.exact && !isActive) continue

    const { hash, query, matched, href } = resolved
    const queryLen = Object.keys(query).length

    if (rp.exact) {
      if (hash !== currentHash) continue
      if (queryLen !== currentQueryLen || !includesParams(currentQuery, query)) continue
      name = rp.name
      break
    }

    if (hash !== '' && hash !== currentHash) continue
    if (queryLen !== 0 && !includesParams(query, currentQuery)) continue

    const score = {
      matchedLen: matched.length,
      queryDiff: currentQueryLen - queryLen,
      hrefLen: href.length - hash.length
    }
    if (
      score.matchedLen > best.matchedLen
      || (score.matchedLen === best.matchedLen && score.queryDiff < best.queryDiff)
      || (score.matchedLen === best.matchedLen && score.queryDiff === best.queryDiff && score.hrefLen > best.hrefLen)
    ) {
      name = rp.name
      best = score
    }
  }

  if (name !== null && name !== props.modelValue) emit('update:modelValue', name)
}

const registerRoutePanel = rp => {
  routePanels.value.push(rp)
  if (routePanels.value.length === 1) {
    routeWatchStop = watch(() => $route.fullPath, () => setTimeout(updateActiveRoute, 0))
    setTimeout(updateActiveRoute, 0)
  }
}

const unregisterRoutePanel = name => {
  routePanels.value = routePanels.value.filter(p => p.name !== name)
  if (!routePanels.value.length) {
    routeWatchStop?.()
    routeWatchStop = null
  }
}

useSwipe(
  rootRef,
  evt => goToPanelByOffset(evt.direction === (props.vertical ? 'up' : 'left') ? 1 : -1),
  { enabled: computed(() => props.swipeable), vertical: computed(() => props.vertical) }
)

watch(() => props.modelValue, (newVal, oldVal) => {
  prevActive.value = oldVal
  emit('beforeTransition', newVal, oldVal)
  clearTimeout(transitionTimer)
  transitionTimer = setTimeout(() => emit('transition', newVal, oldVal), props.transitionDuration)
})

onBeforeUnmount(() => {
  routeWatchStop?.()
  clearTimeout(transitionTimer)
})

provide('panels', {
  active: computed(() => props.modelValue),
  prevActive,
  animated: computed(() => props.animated),
  vertical: computed(() => props.vertical),
  infinite: computed(() => props.infinite),
  transitionDuration: computed(() => props.transitionDuration),
  panels,
  register,
  unregister,
  registerRoutePanel,
  unregisterRoutePanel
})

defineExpose({
  next: () => goToPanelByOffset(1),
  previous: () => goToPanelByOffset(-1),
  goTo
})
</script>

<style scoped>
@reference "#app-styles.css";

.panels {
  @apply grid relative;
  /* Without an explicit column track, the grid's implicit column defaults to
     auto-sizing (content-based) — it silently expands to fit whichever stacked
     .panels--frame is widest instead of being capped at the available width.
     minmax(0, 1fr) caps it and lets it shrink below content size, same fix as
     flexbox's min-width: auto trap. Without this, wide-content panels overflow
     past this container's own bounds; clip-path below then visually hides that
     overflow but also clips interactive elements inside it from hit-testing —
     they exist in the DOM but silently become unclickable. */
  grid-template-columns: minmax(0, 1fr);
  /* clip-path (not overflow-hidden) clips the slide transition without becoming a scroll
     container, so position: sticky still works inside descendants (e.g. DataGrid headers) */
  clip-path: inset(0);
}
</style>
